import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  fetchAdminTenancyMutations,
  createAdminTenancyParticipantMutation,
  fetchAdminTenants,
} from "../../../api/adminData";
import {
  formatDateOnly,
  formatDateTime,
  todayBusinessDate,
} from "../../../utils/datetime";
import { inputClass, labelClass } from "./tenantDetailStyles";
import TenantParticipantPicker from "./TenantParticipantPicker";

/**
 * Tenancy participant mutations (Cluster A / A3b.2b).
 *
 * Self-contained, tenancyId-driven (mirrors the RentalContractSection pattern).
 * Lets an admin ADD a co-tenant or record a co-tenant's EXIT via the additive
 * mutation endpoint, and shows the mutation (Nachtrag) history. The primary
 * tenant is never offered for add or end here (the backend protects it too).
 *
 * After a successful mutation it reloads the history AND calls onChanged() so
 * the parent (AdminTenantDetailPage) refetches tenancies and the live roster.
 */

const STATUS_LABELS_DE = {
  draft: "Entwurf",
  generated: "Erstellt",
  signed: "Signiert",
  cancelled: "Storniert",
};

function todayIso() {
  return todayBusinessDate();
}

function tenantLabel(t) {
  if (!t) return "—";
  const full = [t.first_name, t.last_name].filter(Boolean).join(" ").trim();
  return t.display_name || full || t.name || String(t.id || "");
}

function participantLabel(p) {
  const t = (p && p.tenant) || {};
  const full = [t.first_name, t.last_name].filter(Boolean).join(" ").trim();
  return t.name || full || String((p && p.tenant_id) || "");
}

export default function TenancyParticipantMutationsSection({
  tenancyId,
  tenancy,
  participants,
  onChanged,
}) {
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const [mode, setMode] = useState(null); // null | "add" | "end"
  const [effectiveDate, setEffectiveDate] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // add-mode
  const [addRole, setAddRole] = useState("co_tenant");
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // end-mode
  const [endTenantId, setEndTenantId] = useState("");

  const moveInIso = tenancy && tenancy.move_in_date ? String(tenancy.move_in_date) : null;

  const list = useMemo(
    () => (Array.isArray(participants) ? participants : []),
    [participants]
  );

  // Active, non-primary participants are the only ones that can be ended.
  const activeCoTenants = useMemo(
    () =>
      list.filter(
        (p) =>
          p &&
          p.role !== "primary_tenant" &&
          String(p.status || "active") === "active" &&
          !p.valid_to
      ),
    [list]
  );

  // tenant_ids already on the tenancy (any status) — excluded from the picker
  // (re-add of an existing participant is rejected by the backend).
  const existingTenantIds = useMemo(
    () => new Set(list.map((p) => String(p && p.tenant_id))),
    [list]
  );

  const loadHistory = useCallback(async () => {
    if (!tenancyId) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await fetchAdminTenancyMutations(tenancyId);
      setHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      setHistoryError(e?.message || "Mutationen konnten nicht geladen werden.");
    } finally {
      setHistoryLoading(false);
    }
  }, [tenancyId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  // Debounced existing-tenant search for the add picker; excludes tenants that
  // are already participants of this tenancy.
  useEffect(() => {
    if (mode !== "add") return;
    const q = String(searchQuery || "").trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError(null);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    setSearchError(null);
    const handle = setTimeout(() => {
      fetchAdminTenants({ q, limit: 8 })
        .then((data) => {
          if (cancelled) return;
          const items = Array.isArray(data)
            ? data
            : Array.isArray(data?.items)
              ? data.items
              : [];
          setSearchResults(
            items.filter((x) => !existingTenantIds.has(String(x.id)))
          );
        })
        .catch((e) => {
          if (cancelled) return;
          setSearchError(e?.message || "Mieter konnten nicht geladen werden.");
          setSearchResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearchLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [mode, searchQuery, existingTenantIds]);

  const resetModal = () => {
    setMode(null);
    setEffectiveDate("");
    setReason("");
    setFormError(null);
    setAddRole("co_tenant");
    setSelectedTenant(null);
    setSearchQuery("");
    setSearchResults([]);
    setSearchLoading(false);
    setSearchError(null);
    setEndTenantId("");
  };

  const openAdd = () => {
    resetModal();
    setEffectiveDate(moveInIso || todayIso());
    setMode("add");
  };

  const openEnd = () => {
    resetModal();
    setEffectiveDate(todayIso());
    setMode("end");
  };

  const submit = async (payload) => {
    setFormError(null);
    setSaving(true);
    try {
      await createAdminTenancyParticipantMutation(tenancyId, payload);
      toast.success("Mutation gespeichert.");
      resetModal();
      await loadHistory();
      if (onChanged) onChanged();
    } catch (e) {
      const msg = e?.message || "Speichern fehlgeschlagen.";
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const submitAdd = () => {
    if (!effectiveDate) {
      setFormError("Bitte ein Datum wählen.");
      return;
    }
    if (!selectedTenant || selectedTenant.id == null) {
      setFormError("Bitte einen bestehenden Mieter auswählen.");
      return;
    }
    if (addRole !== "co_tenant" && addRole !== "solidarhafter") {
      setFormError("Bitte eine Rolle wählen (Co-Mieter oder Solidarhafter).");
      return;
    }
    void submit({
      effective_date: effectiveDate,
      reason: String(reason || "").trim() || null,
      add: [{ tenant_id: String(selectedTenant.id), role: addRole }],
    });
  };

  const submitEnd = () => {
    if (!effectiveDate) {
      setFormError("Bitte ein Datum wählen.");
      return;
    }
    if (!endTenantId) {
      setFormError("Bitte einen Co-Mieter wählen.");
      return;
    }
    void submit({
      effective_date: effectiveDate,
      reason: String(reason || "").trim() || null,
      end: [{ tenant_id: String(endTenantId) }],
    });
  };

  return (
    <div className="mt-4" data-testid="tenancy-participant-mutations">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.8px] text-[#64748b] dark:text-[#6b7a9a]">
          Mutationen / Nachträge
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={openAdd}
            disabled={saving}
            className="rounded-[8px] border border-black/10 bg-transparent px-3 py-1.5 text-[12px] font-semibold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04]"
            style={{ cursor: saving ? "default" : "pointer" }}
          >
            Co-Mieter hinzufügen
          </button>
          <button
            type="button"
            onClick={openEnd}
            disabled={saving || activeCoTenants.length === 0}
            title={
              activeCoTenants.length === 0
                ? "Keine aktiven Co-Mieter zum Beenden"
                : undefined
            }
            className="rounded-[8px] border border-black/10 bg-transparent px-3 py-1.5 text-[12px] font-semibold text-[#64748b] hover:bg-slate-100 disabled:opacity-50 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04]"
            style={{
              cursor:
                saving || activeCoTenants.length === 0 ? "default" : "pointer",
            }}
          >
            Austritt erfassen
          </button>
        </div>
      </div>

      {formError ? (
        <p className="mb-2 text-[12px] text-[#f87171]">{formError}</p>
      ) : null}

      {mode === "add" ? (
        <div className="mb-3 rounded-[10px] border border-black/10 p-3 dark:border-white/[0.07]">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.8px] text-[#64748b] dark:text-[#6b7a9a]">
            Co-Mieter hinzufügen
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "10px",
            }}
          >
            <div>
              <label htmlFor="mut-add-date" className={labelClass}>
                Wirksam ab *
              </label>
              <input
                id="mut-add-date"
                type="date"
                className={inputClass}
                value={effectiveDate}
                min={moveInIso || undefined}
                onChange={(e) => setEffectiveDate(e.target.value)}
                disabled={saving}
              />
            </div>
            <div>
              <label htmlFor="mut-add-role" className={labelClass}>
                Rolle *
              </label>
              <select
                id="mut-add-role"
                className={inputClass}
                value={addRole}
                onChange={(e) => setAddRole(e.target.value)}
                disabled={saving}
              >
                <option value="co_tenant">Co-Mieter</option>
                <option value="solidarhafter">Solidarhafter</option>
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label className={labelClass}>Grund (optional)</label>
              <input
                id="mut-add-reason"
                type="text"
                className={inputClass}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={saving}
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <TenantParticipantPicker
                query={searchQuery}
                onQueryChange={setSearchQuery}
                results={searchResults}
                loading={searchLoading}
                error={searchError}
                selectedTenant={selectedTenant}
                onSelect={(t) => {
                  setSelectedTenant(t);
                  setFormError(null);
                }}
                onClear={() => {
                  setSelectedTenant(null);
                  setSearchQuery("");
                  setSearchResults([]);
                }}
                disabled={saving}
              />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={submitAdd}
              disabled={saving}
              className="rounded-[8px] bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] px-3.5 py-2 text-[13px] font-semibold text-white"
              style={{ border: "none", cursor: saving ? "default" : "pointer" }}
            >
              {saving ? "Speichern …" : "Hinzufügen"}
            </button>
            <button
              type="button"
              onClick={resetModal}
              disabled={saving}
              className="rounded-[8px] border border-black/10 bg-transparent px-3.5 py-2 text-[13px] font-semibold text-[#64748b] dark:border-white/[0.1] dark:text-[#8090b0]"
              style={{ cursor: saving ? "default" : "pointer" }}
            >
              Abbrechen
            </button>
          </div>
        </div>
      ) : null}

      {mode === "end" ? (
        <div className="mb-3 rounded-[10px] border border-black/10 p-3 dark:border-white/[0.07]">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.8px] text-[#64748b] dark:text-[#6b7a9a]">
            Austritt erfassen
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "10px",
            }}
          >
            <div>
              <label htmlFor="mut-end-tenant" className={labelClass}>
                Co-Mieter *
              </label>
              <select
                id="mut-end-tenant"
                className={inputClass}
                value={endTenantId}
                onChange={(e) => setEndTenantId(e.target.value)}
                disabled={saving}
              >
                <option value="">— wählen</option>
                {activeCoTenants.map((p) => (
                  <option key={String(p.tenant_id)} value={String(p.tenant_id)}>
                    {participantLabel(p)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="mut-end-date" className={labelClass}>
                Austritt per *
              </label>
              <input
                id="mut-end-date"
                type="date"
                className={inputClass}
                value={effectiveDate}
                min={moveInIso || undefined}
                onChange={(e) => setEffectiveDate(e.target.value)}
                disabled={saving}
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label className={labelClass}>Grund (optional)</label>
              <input
                id="mut-end-reason"
                type="text"
                className={inputClass}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={submitEnd}
              disabled={saving}
              className="rounded-[8px] bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] px-3.5 py-2 text-[13px] font-semibold text-white"
              style={{ border: "none", cursor: saving ? "default" : "pointer" }}
            >
              {saving ? "Speichern …" : "Austritt speichern"}
            </button>
            <button
              type="button"
              onClick={resetModal}
              disabled={saving}
              className="rounded-[8px] border border-black/10 bg-transparent px-3.5 py-2 text-[13px] font-semibold text-[#64748b] dark:border-white/[0.1] dark:text-[#8090b0]"
              style={{ cursor: saving ? "default" : "pointer" }}
            >
              Abbrechen
            </button>
          </div>
        </div>
      ) : null}

      <div className="mt-1">
        {historyError ? (
          <p className="m-0 text-[12px] text-[#f87171]">{historyError}</p>
        ) : historyLoading ? (
          <p className="m-0 text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
            Lade Mutationen …
          </p>
        ) : history.length === 0 ? (
          <p className="m-0 text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
            Keine Mutationen erfasst.
          </p>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0">
            {history.map((m) => (
              <li
                key={String(m.id)}
                className="rounded-[8px] border border-black/10 p-2.5 dark:border-white/[0.07]"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[12px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
                    {m.summary || "Mutation"}
                  </span>
                  <span className="inline-flex rounded-full border border-black/10 bg-slate-100 px-[6px] py-[1px] text-[9px] font-semibold text-[#64748b] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#8090b0]">
                    {STATUS_LABELS_DE[m.status] || m.status}
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-[#64748b] dark:text-[#8090b0]">
                  Wirksam ab:{" "}
                  {m.effective_date ? formatDateOnly(m.effective_date) : "—"}
                </div>
                {m.reason ? (
                  <div className="text-[11px] italic text-[#64748b] dark:text-[#6b7a9a]">
                    Grund: {m.reason}
                  </div>
                ) : null}
                <div className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                  Erfasst: {m.created_at ? formatDateTime(m.created_at) : "—"}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
